import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import API_BASE_URL from "../RestAPI";
import { showErrorToast } from "../../constants/ToastService.js";

// Logs the user out when an API call that carried their token comes back 401
// (expired or rejected). Only the server decides: the token's "exp" is not
// checked here, so a wrong PC clock can't log anyone out early.

const API_ROOT = API_BASE_URL.replace(/\/$/, "");
let onExpired = null; // set while Layout is mounted
let expiring = false; // one logout (and one toast) however many calls fail together

const expireSession = () => {
  if (expiring || !localStorage.getItem("authToken")) return;
  expiring = true;
  if (onExpired) onExpired();
  else {
    localStorage.clear();
    window.location.assign("/");
  }
};

const sentBearer = (input, init) => {
  const h = init?.headers || (input instanceof Request ? input.headers : null);
  if (!h) return false;
  const v = h instanceof Headers ? h.get("Authorization") : h.Authorization || h.authorization;
  return typeof v === "string" && v.startsWith("Bearer ");
};

// There is no shared HTTP client, so wrap fetch once: a 401 from our own API on
// a call that sent the token means the session is over. The login call sends
// no token, so a wrong password never logs anyone out.
if (typeof window !== "undefined" && !window.__fdSessionFetch) {
  window.__fdSessionFetch = true;
  const nativeFetch = window.fetch.bind(window);
  window.fetch = async (input, init) => {
    const res = await nativeFetch(input, init);
    if (res.status === 401) {
      const url = typeof input === "string" ? input : input?.url || "";
      if (url.startsWith(API_ROOT) && sentBearer(input, init)) expireSession();
    }
    return res;
  };
}

const useSessionExpiry = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    // A fresh login lands on a new route with a new token: allow the next logout.
    if (localStorage.getItem("authToken")) expiring = false;
    onExpired = () => {
      localStorage.clear();
      showErrorToast("Session expired. Please log in again.");
      navigate("/", { replace: true });
    };
    return () => { onExpired = null; };
  }, [navigate, pathname]);
};

export default useSessionExpiry;